import { gql } from "@apollo/client";
import { useAvailableLanguages } from "@src/features/contentDetail/hooks/useAvailableLanguages";
import {
  UploadedTrainingResourceFragment,
  useBulkCreateTrainingResourcesMutation,
} from "@src/features/app/dialogs/bulkUploadTrainingResourceDialog/BulkUploadTrainingResourceDialog.generated";
import { UploadedFileList } from "@src/features/app/dialogs/bulkUploadTrainingResourceDialog/UploadedFileList";
import UploadFilesDefaultState from "@src/features/app/dialogs/bulkUploadTrainingResourceDialog/UploadFilesDefaultState";
import { Button } from "@src/lib/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@src/lib/ui/Dialog";
import { Label } from "@src/lib/ui/Label";
import { Switch } from "@src/lib/ui/Switch";
import Text from "@src/lib/ui/Text";
import TranslationBanner from "@src/features/contentDetail/ui/TranslationBanner";
import { useS3Upload, S3Upload } from "@src/features/app/hooks/useS3Upload";
import { useToast } from "@src/lib/hooks/useToast";
import {
  BulkCreateTrainingResourceInput,
  Ff,
  GraphqlOperations,
  TranslationSetInput,
} from "@src/types.generated";
import { launchConfetti } from "@src/lib/utils/confetti";
import { pluralize, uuid4 } from "@src/lib/utils/strings";
import { AnimatePresence, motion } from "framer-motion";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { updateTranslationSet } from "@src/features/contentDetail/utils/translationSets";
import { emptyTranslationSet } from "@src/features/knowledgeCheck/utils/prompts";
import { useDropzone } from "react-dropzone";
import { useRouter } from "next/router";
import TrainingResourceBulkUploadConfirmationDialog from "./TrainingResourceBulkUploadConfirmationDialog";
import { ACCEPTED_FILE_TYPES_GROUPED } from "@src/lib/utils/files";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { cn } from "@src/lib/utils/cn";
import { useBulkCreateLibraryItemFolderMembershipsMutation } from "@src/features/contentLibrary/AddToFolderMultiSelectLibraryItemAction.generated";

export type FileInput = {
  id: string;
  name: TranslationSetInput;
  url?: string;
  size?: number;
  description?: TranslationSetInput;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folderId?: string;
  folderName?: string;
  onPdfFileUploaded?: (file: File) => void;
  onFilesUploaded?: (files: UploadedTrainingResourceFragment[]) => void;
  setSelectedLibraryItemIdsFromBulkUpload?: (ids: string[]) => void;
  setSwitchingToResourceTabFromBulkUpload?: (value: boolean) => void;
};

const BulkUploadTrainingResourceDialog: FC<Props> = (props) => {
  const isRichTextDocumentEnabled = useFeatureFlag(Ff.RichTextDocument);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationCount, setConfirmationCount] = useState(0);

  useEffect(() => {
    window.addEventListener("drop", (e) => e.preventDefault());
  }, []);
  const router = useRouter();
  const [files, setFiles] = useState<FileInput[]>([]);

  const onUploadComplete = useCallback((upload: S3Upload) => {
    setFiles((files) => {
      return files.map((file) => {
        if (file.id === upload.fileId) {
          return {
            ...file,
            url: upload.url,
          };
        }
        return file;
      });
    });
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[upload.fileId];
      return newProgress;
    });
  }, []);

  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>(
    {},
  );

  const onUploadProgress = useCallback((fileId: string, progress: number) => {
    setUploadProgress((prev) => ({
      ...prev,
      [fileId]: progress,
    }));
  }, []);

  const getUploadProgress = useCallback(
    (fileId: string) => {
      return uploadProgress[fileId] || 0;
    },
    [uploadProgress],
  );

  const { addErrorToast, addToast } = useToast();

  const [bulkCreateMemberships] =
    useBulkCreateLibraryItemFolderMembershipsMutation({
      refetchQueries: [
        GraphqlOperations.Query.FolderLibraryItem,
        GraphqlOperations.Query.LibraryAllContentTable_LibraryItems,
        GraphqlOperations.Query.ResourcesLibraryPaginatedTable_LibraryItems,
      ],
    });

  const onUploadFailed = useCallback((error: Error, fileId: string) => {
    setFiles((files) => {
      return files.filter((file) => file.id !== fileId);
    });
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[fileId];
      return newProgress;
    });
  }, []);

  const { handleFileUpload, isUploading, uploadingFiles } = useS3Upload({
    onUploadComplete,
    onUploadProgress,
    onUploadFailed,
  });

  const updateFileName = useCallback(
    (id: string, lang: Language, name: string) => {
      setFiles((files) => {
        return files.map((file) => {
          if (file.id === id) {
            return {
              ...file,
              name: updateTranslationSet({
                ts: file.name,
                lang,
                text: name,
              }),
            };
          }
          return file;
        });
      });
    },
    [setFiles],
  );
  const updateFileDescription = useCallback(
    (id: string, language: Language, description: string) => {
      setFiles((files) => {
        return files.map((file) => {
          if (file.id === id) {
            return {
              ...file,
              description: updateTranslationSet({
                ts: file.description || emptyTranslationSet(),
                lang: language,
                text: description,
              }),
            };
          }
          return file;
        });
      });
    },
    [setFiles],
  );

  const removeFile = useCallback(
    (id: string) => {
      setFiles((files) => {
        return files.filter((file) => file.id !== id);
      });
      setUploadProgress((prev) => {
        const newProgress = { ...prev };
        delete newProgress[id];
        return newProgress;
      });
    },
    [setFiles],
  );

  const onFilesSelected = useCallback(
    (files: File[]) => {
      const currentUploads = Array.from(files).map((file) => ({
        id: uuid4(),
        name: { en: file.name, translationOverrides: [] },
        size: file.size,
      }));
      setFiles((files) => [...files, ...currentUploads]);

      currentUploads.forEach((fileInput, index) => {
        handleFileUpload(files[index], fileInput.id);
      });
    },
    [handleFileUpload],
  );

  const onAddLink = useCallback(
    (url: string) => {
      setFiles([
        {
          id: uuid4(),
          name: { en: url, translationOverrides: [] },
          url,
        },
      ]);
    },
    [setFiles],
  );

  const { mostPopularNonEnglishLanguage } = useAvailableLanguages();
  const [translationLanguage, setTranslationLanguage] = useState(
    mostPopularNonEnglishLanguage,
  );
  const [translationModeEnabled, setTranslationModeEnabled] = useState(false);
  const inputs: BulkCreateTrainingResourceInput[] = useMemo(() => {
    return files.map((file) => ({
      mediaUrls: {
        en: file.url || "",
        translationOverrides: [],
      },
      name: file.name,
      description: file.description,
    }));
  }, [files]);
  const [createTrainingResources, { loading: mutationLoading }] =
    useBulkCreateTrainingResourcesMutation({
      variables: { inputs },
      onCompleted: async (data) => {
        const uploadedFiles =
          data.bulkCreateTrainingResources.trainingResources;
        if (data.bulkCreateTrainingResources.success && uploadedFiles) {
          if (props.folderId && uploadedFiles.length > 0) {
            const folderId = props.folderId;
            const libraryItemIds = uploadedFiles.map((r) => r.libraryItem.id);
            const inputs = libraryItemIds.map((libraryItemId) => ({
              folderId,
              libraryItemId,
            }));

            try {
              const membershipResult = await bulkCreateMemberships({
                variables: { inputs },
              });
              if (
                membershipResult.data?.createLibraryItemFolderMemberships
                  .success
              ) {
                addToast({
                  message: `Resources added to folder successfully`,
                });
              } else {
                addErrorToast({
                  ...membershipResult.data,
                  callsite: "bulk_upload_add_to_folder",
                });
              }
            } catch (error) {
              addErrorToast({
                error,
                callsite: "bulk_upload_add_to_folder",
              });
            }
          }

          if (
            props.setSwitchingToResourceTabFromBulkUpload &&
            props.setSelectedLibraryItemIdsFromBulkUpload
          ) {
            router.push({
              pathname: router.pathname,
              query: { ...router.query, tab: "resources" },
            });
            props.setSwitchingToResourceTabFromBulkUpload(true);
            const libraryItemIds =
              uploadedFiles.map((r) => r.libraryItem.id) || [];
            props.setSelectedLibraryItemIdsFromBulkUpload(libraryItemIds);
          }
          props.onOpenChange(false);
          launchConfetti();
          if (
            props.setSwitchingToResourceTabFromBulkUpload &&
            props.setSelectedLibraryItemIdsFromBulkUpload
          ) {
            setConfirmationCount(uploadedFiles.length);
            setShowConfirmation(true);
          }
          props.onFilesUploaded?.(uploadedFiles);
        } else {
          addErrorToast({ ...data, callsite: "bulk_upload_files" });
        }
      },
      refetchQueries: [
        GraphqlOperations.Query.ResourcesLibraryPaginatedTable_LibraryItems,
      ],
    });
  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[]) => {
      if (acceptedFiles.length === 0) return;

      if (
        isRichTextDocumentEnabled &&
        acceptedFiles.length === 1 &&
        acceptedFiles[0].type === "application/pdf" &&
        !!props.onPdfFileUploaded
      ) {
        props.onPdfFileUploaded?.(acceptedFiles[0]);
        props.onOpenChange(false);
        return;
      }

      if (acceptedFiles.length !== 0) {
        onFilesSelected(acceptedFiles);
      }
    },
    [isRichTextDocumentEnabled, onFilesSelected, props],
  );
  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    useFsAccessApi: true,
    multiple: true,
    noClick: true,
    noKeyboard: true,
    onDrop,
    accept: ACCEPTED_FILE_TYPES_GROUPED,
  });
  const dialogTitle = props.folderName
    ? `Add Resources to ${props.folderName}`
    : "Add Resources";

  return (
    <>
      <Dialog open={props.open} onOpenChange={props.onOpenChange}>
        <DialogContent className="flex w-auto max-w-none flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border p-4">
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <div {...getRootProps()}>
            <input {...getInputProps()} className="hidden" />
            <AnimatePresence initial={false}>
              <motion.div
                animate={files.length === 0 ? "empty" : "full"}
                variants={{
                  empty: {
                    height: 370,
                    transition: {
                      delay: 0,
                      duration: 0.3,
                    },
                  },
                  full: {
                    height: 500,
                    transition: {
                      delay: 0,
                      duration: 0.3,
                    },
                  },
                }}
                className="bg-gray-3-alpha"
              >
                {files.length === 0 && (
                  <div className="h-full p-2.5">
                    <UploadFilesDefaultState
                      openFileSelector={open}
                      onAddLink={onAddLink}
                      isDragActive={isDragActive}
                    />
                  </div>
                )}
                {files.length > 0 && (
                  <motion.div
                    className="flex h-full flex-col"
                    animate={
                      translationModeEnabled
                        ? "translationModeEnabled"
                        : "translationModeDisabled"
                    }
                    variants={{
                      translationModeEnabled: {
                        width: 800,
                        transition: {
                          delay: 0,
                          duration: 0.3,
                        },
                      },
                      translationModeDisabled: {
                        width: 500,
                        transition: {
                          delay: 0,
                          duration: 0.3,
                        },
                      },
                    }}
                  >
                    <div className="shrink-0">
                      <TranslationBanner
                        translationModeEnabled={translationModeEnabled}
                        selectedLanguage={translationLanguage}
                        onLanguageChange={setTranslationLanguage}
                      />
                    </div>
                    <div
                      className={cn("min-h-0 flex-1 overflow-y-auto", {
                        "grid grid-cols-2 gap-4":
                          translationModeEnabled && translationLanguage,
                      })}
                    >
                      <UploadedFileList
                        files={files}
                        updateFileName={updateFileName}
                        updateFileDescription={updateFileDescription}
                        removeFile={removeFile}
                        language={"en"}
                        getUploadProgress={getUploadProgress}
                        uploadingFiles={uploadingFiles}
                      />
                      {translationModeEnabled && translationLanguage && (
                        <UploadedFileList
                          files={files}
                          updateFileName={updateFileName}
                          updateFileDescription={updateFileDescription}
                          removeFile={removeFile}
                          language={translationLanguage}
                          getUploadProgress={getUploadProgress}
                          uploadingFiles={uploadingFiles}
                        />
                      )}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
          {files.length > 0 && (
            <DialogFooter className="shrink-0 flex-row items-center justify-between border-t border-border p-3 sm:flex-row sm:justify-between sm:space-x-0">
              <div className="flex items-center">
                <Text variant="compact-description">
                  {files.length} {pluralize("file", files.length)}
                </Text>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Label>Translate</Label>
                  <Switch
                    size="sm"
                    checked={translationModeEnabled}
                    onCheckedChange={() => {
                      if (!translationModeEnabled && !translationLanguage) {
                        setTranslationLanguage(mostPopularNonEnglishLanguage);
                      }
                      setTranslationModeEnabled(!translationModeEnabled);
                    }}
                  />
                </div>
                <Button
                  size="lg"
                  onClick={() => createTrainingResources()}
                  loading={isUploading || mutationLoading}
                >
                  Create Resources
                </Button>
              </div>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
      <TrainingResourceBulkUploadConfirmationDialog
        open={showConfirmation}
        onOpenChange={setShowConfirmation}
        numberOfResourcesUploaded={confirmationCount}
      />
    </>
  );
};

gql`
  mutation BulkCreateTrainingResources(
    $inputs: [BulkCreateTrainingResourceInput!]!
  ) {
    bulkCreateTrainingResources(inputs: $inputs) {
      success
      trainingResources {
        ...UploadedTrainingResource
      }
    }
  }

  fragment UploadedTrainingResource on TrainingResource {
    id
    publishedVersion {
      id
      media {
        ...Media
      }
    }
    libraryItem {
      ...ModuleBuilderLibraryItem
    }
    ...TrainingResourceBuilder
  }
`;

export default BulkUploadTrainingResourceDialog;
